import React from 'react';
import { Box, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useCondition } from '../../../hooks/questionnaire/useCondition';
import {
  getQuestionTypeIcon,
  type QuestionnaireFormData,
} from '@/features/questionnaires';
import { SelectInput } from '@/shared';

interface ConditionTargetSelectProps {
  questionIndex: number;
  conditionIndex: number;
}

export const ConditionTargetSelect: React.FC<ConditionTargetSelectProps> = ({
  questionIndex: questionIndex,
  conditionIndex: conditionIndex,
}) => {
  const form = useFormContext<QuestionnaireFormData>();
  const { t } = useTranslation();
  const { target, path, previous, initialQuestions, questions } = useCondition(
    questionIndex,
    conditionIndex,
  );
  const targetPath = `${path}.conditions.${conditionIndex}.target` as const;

  const setQuestionLabel = (questionId: string) => {
    switch (target?.source) {
      case 'initial-question': {
        const q = initialQuestions.find((q) => q.id === questionId);
        const label = q?.label ?? '';
        form.setValue(`${targetPath}.questionLabel`, label, {
          shouldDirty: true,
        });
        break;
      }
      case 'question': {
        const q = questions.find((q) => q.id === questionId);
        const label = q?.label ?? '';
        form.setValue(`${targetPath}.questionLabel`, label, {
          shouldDirty: true,
        });
        break;
      }
      default: {
        form.setValue(`${targetPath}.questionLabel`, '', {
          shouldDirty: true,
        });
        break;
      }
    }
  };

  return (
    <>
      <SelectInput
        form={form}
        name={`${targetPath}.source`}
        label={t('pages.studies.questions.conditions.targetType')}
        sx={{ minWidth: '180px' }}
      >
        <MenuItem value="question" disabled={!previous.length}>
          {t('pages.studies.questions.conditions.questionTarget')}
        </MenuItem>
        <MenuItem value="initial-question" disabled={!initialQuestions.length}>
          {t('pages.studies.questions.conditions.initialQuestionTarget')}
        </MenuItem>
        <MenuItem value="patientData">
          {t('pages.studies.questions.conditions.patientDataTarget')}
        </MenuItem>
      </SelectInput>

      {target?.source === 'question' && (
        <SelectInput
          form={form}
          name={`${targetPath}.questionId`}
          label={t('pages.studies.questions.conditions.selectQuestion')}
          sx={{ minWidth: '180px' }}
          onChange={(e) => setQuestionLabel(e.target.value as string)}
        >
          {previous.length === 0 ? (
            <MenuItem disabled>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                {t('pages.studies.questions.conditions.noPreviousQuestions')}
              </Typography>
            </MenuItem>
          ) : (
            previous.map((q, idx) => (
              <MenuItem key={q.id} value={q.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getQuestionTypeIcon(q.parameters.type)}
                  {q.label ||
                    `Question ${idx + 1} - ${t('pages.studies.questions.conditions.noLabelYet')}`}
                </Box>
              </MenuItem>
            ))
          )}
        </SelectInput>
      )}

      {target?.source === 'initial-question' && (
        <SelectInput
          form={form}
          name={`${targetPath}.questionId`}
          label={t('pages.studies.questions.conditions.selectQuestion')}
          sx={{ minWidth: '180px' }}
          onChange={(e) => setQuestionLabel(e.target.value as string)}
        >
          {initialQuestions.length === 0 ? (
            <MenuItem disabled>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                {t('pages.studies.questions.conditions.noInitialQuestions')}
              </Typography>
            </MenuItem>
          ) : (
            initialQuestions.map((q, idx) => (
              <MenuItem key={q.id} value={q.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getQuestionTypeIcon(q.parameters.type)}
                  {q.label ||
                    `Initial Question ${idx + 1} - ${t('pages.studies.questions.conditions.noLabelYet')}`}
                </Box>
              </MenuItem>
            ))
          )}
        </SelectInput>
      )}

      {target?.source === 'patientData' && (
        <SelectInput
          form={form}
          name={`${targetPath}.field`}
          label={t('pages.studies.questions.conditions.selectField')}
          sx={{ minWidth: '180px' }}
          onChange={(e) => setQuestionLabel(e.target.value as string)}
        >
          <MenuItem value="birthdate">
            {t('pages.studies.questions.conditions.birthdate')}
          </MenuItem>
          <MenuItem value="gender">
            {t('pages.studies.questions.conditions.gender')}
          </MenuItem>
          <MenuItem value="chronicIllnesses">
            {t('pages.studies.questions.conditions.chronicIllnesses')}
          </MenuItem>
        </SelectInput>
      )}
    </>
  );
};
